// client/src/components/pages/LexiStar/Routes/routeGeometry.js
//
// Pure helpers for the Routes & lanes editor (ST-00188 M2-A1): route length and
// trimming for golden runs, the corridor band's MapLibre paint (a line whose
// width is the corridor in metres at every zoom — an exact, instant live
// preview of the buffer while the slider moves), geofence features for the map,
// and version-to-form mapping. GeoJSON order ([lon, lat]) throughout.
//
// Corridor width semantics match the server and @lexistar/sim: corridor_m is
// the FULL width; the engine's compliance test uses half of it each side.

import { circleToPolygonRing, polygonRingFromLatLngs } from '../../../map/geo';
import { getPurposeMeta } from '../../Geofencing/purposeTypes';

const EARTH_MEAN_RADIUS_M = 6371008.8;
const EARTH_CIRCUMFERENCE_M = 40075016.686;
const DEG = Math.PI / 180;

export const CORRIDOR_MIN_M = 50;
export const CORRIDOR_MAX_M = 5000;
export const DEFAULT_CORRIDOR_M = 300;

export function haversineM([lon1, lat1], [lon2, lat2]) {
  const dLat = (lat2 - lat1) * DEG;
  const dLon = (lon2 - lon1) * DEG;
  const s =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * DEG) * Math.cos(lat2 * DEG) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_MEAN_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(s)));
}

/** Length of a [lon,lat][] line in km (2 decimals). */
export function lineKm(coords) {
  if (!Array.isArray(coords) || coords.length < 2) return 0;
  let m = 0;
  for (let i = 1; i < coords.length; i++) m += haversineM(coords[i - 1], coords[i]);
  return Math.round(m / 10) / 100;
}

/** Keep the part of a line between two fractions (0..1) of its length. */
export function trimLine(coords, fromFraction = 0, toFraction = 1) {
  if (!Array.isArray(coords) || coords.length < 2) return coords || [];
  const f0 = Math.max(0, Math.min(1, fromFraction));
  const f1 = Math.max(f0, Math.min(1, toFraction));
  if (f0 === 0 && f1 === 1) return coords.slice();
  const cumulative = [0];
  for (let i = 1; i < coords.length; i++) {
    cumulative.push(cumulative[i - 1] + haversineM(coords[i - 1], coords[i]));
  }
  const total = cumulative[cumulative.length - 1];
  const at = (m) => {
    let i = 1;
    while (i < cumulative.length - 1 && cumulative[i] < m) i++;
    const seg = cumulative[i] - cumulative[i - 1];
    const t = seg === 0 ? 0 : (m - cumulative[i - 1]) / seg;
    const a = coords[i - 1];
    const b = coords[i];
    return { i, p: [a[0] + t * (b[0] - a[0]), a[1] + t * (b[1] - a[1])] };
  };
  const start = at(f0 * total);
  const end = at(f1 * total);
  const out = [start.p];
  for (let i = start.i; i < end.i; i++) out.push(coords[i]);
  out.push(end.p);
  // Drop a zero-length duplicate at either end.
  return out.filter((p, i) => i === 0 || p[0] !== out[i - 1][0] || p[1] !== out[i - 1][1]);
}

/** Mean latitude of a line (the corridor paint's reference latitude). */
export function meanLat(coords) {
  if (!Array.isArray(coords) || !coords.length) return 0;
  return coords.reduce((s, c) => s + c[1], 0) / coords.length;
}

/**
 * MapLibre `line-width` expression drawing `widthM` metres at every zoom at
 * latitude `lat` (512 px tiles: metres per pixel = C·cos(lat) / (512·2^z)).
 * Exponential base 2 between zoom 0 and 24 is exact.
 */
export function corridorLineWidth(widthM, lat) {
  const pxAtZ0 = (widthM * 512) / (EARTH_CIRCUMFERENCE_M * Math.cos(lat * DEG));
  return ['interpolate', ['exponential', 2], ['zoom'], 0, pxAtZ0, 24, pxAtZ0 * 2 ** 24];
}

/** [[west, south], [east, north]] of a set of [lon, lat] points, or null. */
export function bboxOf(points) {
  const valid = (points || []).filter(
    (p) => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1])
  );
  if (!valid.length) return null;
  let w = Infinity;
  let s = Infinity;
  let e = -Infinity;
  let n = -Infinity;
  for (const [lon, lat] of valid) {
    w = Math.min(w, lon);
    s = Math.min(s, lat);
    e = Math.max(e, lon);
    n = Math.max(n, lat);
  }
  if (w === e && s === n)
    return [
      [w - 0.01, s - 0.01],
      [e + 0.01, n + 0.01],
    ];
  return [
    [w, s],
    [e, n],
  ];
}

/** A geofence's polygon ring ([lon, lat], closed): versioned Geometry first, else legacy. */
export function geofenceRing(g) {
  const geom = g.Geometry;
  if (geom && geom.type === 'Polygon' && Array.isArray(geom.coordinates?.[0])) {
    return geom.coordinates[0];
  }
  if (geom && geom.type === 'Point' && Array.isArray(geom.coordinates)) {
    return circleToPolygonRing(
      geom.coordinates[0],
      geom.coordinates[1],
      g.RadiusM || g.RadiusMeters || 50
    );
  }
  if (g.GeofenceType === 'POLYGON' && g.Coordinates) {
    try {
      return polygonRingFromLatLngs(JSON.parse(g.Coordinates));
    } catch {
      return null;
    }
  }
  if (g.Longitude != null && g.Latitude != null) {
    return circleToPolygonRing(
      Number(g.Longitude),
      Number(g.Latitude),
      Number(g.RadiusMeters) || 50
    );
  }
  return null;
}

/** The geofence's purpose: versioned PurposeType, else the legacy zone type's. */
export function geofencePurpose(g) {
  if (g.PurposeType) return g.PurposeType;
  if (g.ZoneType === 'weighbridge') return 'weighbridge';
  if (g.ZoneType === 'workshop') return 'maintenance';
  if (g.ZoneType === 'no_go') return 'no_go';
  if (g.ZoneType === 'site_boundary') return 'site';
  if (g.ZoneType === 'stockpile' || g.ZoneType === 'loading_bay') return 'source';
  return 'custom';
}

/**
 * FeatureCollection of the tenant's geofences with the editor's role on each:
 * role = 'stop' (permitted stop), 'weighbridge' (required), or 'none'.
 */
export function geofenceFeatures(geofences, { stopIds = [], weighbridgeIds = [] } = {}) {
  const stops = new Set(stopIds.map(Number));
  const wbs = new Set(weighbridgeIds.map(Number));
  const features = [];
  for (const g of geofences || []) {
    const ring = geofenceRing(g);
    if (!ring || ring.length < 4) continue;
    const id = Number(g.GeofenceID);
    const purpose = geofencePurpose(g);
    features.push({
      type: 'Feature',
      id,
      properties: {
        geofence_id: id,
        name: g.Name,
        purpose,
        fill: getPurposeMeta(purpose).fill,
        role: wbs.has(id) ? 'weighbridge' : stops.has(id) ? 'stop' : 'none',
      },
      geometry: { type: 'Polygon', coordinates: [ring] },
    });
  }
  return { type: 'FeatureCollection', features };
}

/** One point per geofence (its ring's centre), same properties: the zoomed-out dots. */
export function geofenceDots(featureCollection) {
  return {
    type: 'FeatureCollection',
    features: (featureCollection?.features || []).map((f) => {
      const ring = f.geometry.coordinates[0];
      const pts = ring.slice(0, -1);
      const lon = pts.reduce((sum, p) => sum + p[0], 0) / pts.length;
      const lat = pts.reduce((sum, p) => sum + p[1], 0) / pts.length;
      return {
        type: 'Feature',
        id: f.id,
        properties: f.properties,
        geometry: { type: 'Point', coordinates: [lon, lat] },
      };
    }),
  };
}

/** Purposes a controller usually permits as stops on a route (LS-04 §5). */
export const STOP_PURPOSES = ['fuel_station', 'rest_stop', 'depot', 'delay', 'border_post', 'site'];

/** Toggle an id in a list (numbers), keeping order. */
export function toggleId(list, id) {
  const n = Number(id);
  return list.map(Number).includes(n) ? list.filter((x) => Number(x) !== n) : [...list, n];
}

/** The editor's draft from a saved version (or an empty draft). */
export function draftFromVersion(v) {
  return {
    geometry: v?.geometry || null,
    planned_km: v?.planned_km ?? null,
    planned_min: v?.planned_min ?? null,
    corridor_m: v?.corridor_m ?? DEFAULT_CORRIDOR_M,
    permitted_stop_geofence_ids: (v?.permitted_stop_geofence_ids || []).map(Number),
    required_weighbridge_geofence_ids: (v?.required_weighbridge_geofence_ids || []).map(Number),
    toll_plaza_ids: v?.toll_plaza_ids || [],
    risk: v?.risk || '',
    source: v?.source || 'manual',
    source_trip_id: v?.source_trip_id || null,
    notes: '',
  };
}

/**
 * The POST body for a new version from the draft. With `base` (the current
 * version), only what changed is sent — the server copies the rest forward.
 */
export function versionPayload(draft, base = null) {
  const full = {
    geometry: draft.geometry,
    planned_km: draft.planned_km === '' ? null : draft.planned_km,
    planned_min: draft.planned_min === '' ? null : draft.planned_min,
    corridor_m: Number(draft.corridor_m),
    permitted_stop_geofence_ids: draft.permitted_stop_geofence_ids,
    required_weighbridge_geofence_ids: draft.required_weighbridge_geofence_ids,
    toll_plaza_ids: draft.toll_plaza_ids,
    risk: draft.risk || null,
    source: draft.source,
    source_trip_id: draft.source === 'golden_run' ? draft.source_trip_id || null : null,
    notes: draft.notes || null,
  };
  if (!base) return full;
  const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  const out = {};
  const geometryChanged = !same(full.geometry, base.geometry);
  if (geometryChanged) {
    out.geometry = full.geometry;
    out.source = full.source;
    out.source_trip_id = full.source_trip_id;
  }
  for (const k of ['planned_km', 'planned_min']) {
    if (geometryChanged || !same(Number(full[k]), Number(base[k]))) {
      if (full[k] != null) out[k] = Number(full[k]);
    }
  }
  if (!same(full.corridor_m, base.corridor_m)) out.corridor_m = full.corridor_m;
  for (const k of ['permitted_stop_geofence_ids', 'required_weighbridge_geofence_ids']) {
    const a = [...full[k]].map(Number).sort((x, y) => x - y);
    const b = [...(base[k] || [])].map(Number).sort((x, y) => x - y);
    if (!same(a, b)) out[k] = full[k];
  }
  if (!same([...full.toll_plaza_ids].sort(), [...(base.toll_plaza_ids || [])].sort())) {
    out.toll_plaza_ids = full.toll_plaza_ids;
  }
  if ((full.risk || null) !== (base.risk || null)) out.risk = full.risk;
  if (full.notes) out.notes = full.notes;
  return out;
}

/** Whether a version payload (from versionPayload with a base) changes anything. */
export const hasVersionChanges = (payload) => Object.keys(payload).some((k) => k !== 'notes');

/** "1 h 25 min" / "45 min" */
export function formatMinutes(min) {
  if (min == null || Number.isNaN(Number(min))) return '—';
  const m = Math.round(Number(min));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h} h ${r} min` : `${h} h`;
}

/** "12.3 km" */
export function formatKm(km) {
  if (km == null || Number.isNaN(Number(km))) return '—';
  const n = Number(km);
  return `${n >= 100 ? Math.round(n) : n.toFixed(1)} km`;
}
